import React from 'react';
import { G } from '@mobily/ts-belt';
import { z } from 'zod';
import type { NextPage, GetStaticProps, GetStaticPaths } from 'next';
import {
  LandingPageCustom,
  landingPageCustomSchema,
} from '../../../components/LandingPageCustom';
import { serverLogger } from '../../../utils/loggers/server';
import { getCachedEntryByUrl } from '../../../utils/requests/contentstack/cachedRequests';
import {
  type DynamicRoutePath,
  getDynamicPaths,
} from '../../../utils/requests/contentstack/dynamicPath';

type Params = DynamicRoutePath['params'];
const rootPath = '/info';

export const getStaticPaths: GetStaticPaths<Params> = async ({ locales }) => {
  const paths = await getDynamicPaths({
    rootPath,
    locales,
    logger: serverLogger,
    contentType: 'landing_page_custom',
  });

  // Setting `fallback` to false to properly 404 on any page not statically generated
  return { paths, fallback: false };
};

export type InfoPageProps = z.infer<typeof landingPageCustomSchema>;

export const getStaticProps: GetStaticProps<InfoPageProps, Params> = async ({
  params,
  locale,
}) => {
  if (!params) {
    return {
      notFound: true,
    };
  }

  const pageUrl = `${rootPath}/${params.slug.join('/')}`;

  const entryRequest = getCachedEntryByUrl({
    contentType: 'landing_page_custom',
    url: pageUrl,
    language: locale,
    schema: landingPageCustomSchema,
    logger: serverLogger,
  });

  let entryResponse;

  try {
    entryResponse = await entryRequest;
  } catch (error: unknown) {
    serverLogger({
      /**
       * not well-documented, but 141 is the code contentstack returns when an
       * undefined entry is requested. At time of comment, it is accompanied
       * by "The requested entry doesn't exist" text.
       */
      level: G.isObject(error) && error?.error_code === 141 ? 'warn' : 'error',
      team: 'client-onboarding',
      message: `Failed to fetch page data - /info/`,
      context: {
        error,
        page: '/info/[...slug]',
        url: pageUrl,
      },
    });
  }

  if (G.isUndefined(entryResponse)) {
    serverLogger({
      level: 'warn',
      team: 'client-onboarding',
      message: 'Required page data is undefined - /info/',
      context: {
        page: '/info/[...slug]',
        url: pageUrl,
      },
    });

    return {
      notFound: true,
    };
  }

  return {
    props: entryResponse.data,
    // revalidate configures the cache policy, this sets it to cache for 5 minutes
    // https://nextjs.org/docs/pages/building-your-application/deploying/production-checklist#caching
    revalidate: 300,
  };
};

const InfoPage: NextPage<InfoPageProps> = ({ seoData, modules }) => {
  return <LandingPageCustom seoData={seoData} modules={modules} />;
};

export default InfoPage;
